// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
// import MDBadge from "theme/components/MDBadge";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";
import Editor from "react-simple-wysiwyg";

function BroadcastCampaign() {
  const pageTitle = "Messaging Campaigns";
  usePageTitle(pageTitle);
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [openNewCheckDialog, setOpenNewCheckDialog] = useState(false);

  const [refreshPage, setRefreshPage] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [subject, setSubject] = useState(null);
  const [messageBody, setMessageBody] = useState(null);
  const [frequency, setFrequency] = useState(null);
  const [audience, setAudience] = useState(null);
  const [interval, setInterval] = useState(null);
  const [channels, setChannels] = useState(null);
  // const [startTime, setStartTime] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const tableColumns = [
    { Header: "View", accessor: "stats", align: "left" },
    { Header: "Subject", accessor: "subject", align: "left" },
    { Header: "Frequency", accessor: "frequency", align: "left" },
    { Header: "Interval", accessor: "interval", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Added On", accessor: "created", align: "right" },
  ];

  const buildRateRows = (data) => {
    const rows = [];
    data.map((broadcast) => {
      let statusColor = "warning";
      if (broadcast.status) {
        statusColor = "success";
      } else {
        statusColor = "error";
      }

      rows.push({
        subject: broadcast.subject,
        audience: broadcast.audience,
        frequency: broadcast.frequency,
        interval: broadcast.interval ? broadcast.interval : "---",
        status: (
          <MDBox>
            <Icon color={statusColor}>{broadcast.status ? "check_circle" : "cancel"}</Icon>
          </MDBox>
        ),
        stats: (
          <MDBox mt={1} variant="button">
            <MDButton
              size="small"
              variant="contained"
              color="info"
              iconOnly
              circular
              onClick={() => {
                navigate(`/message-campaigns/${broadcast.reference}`);
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                visibility
              </Icon>
            </MDButton>
          </MDBox>
        ),
        created: broadcast.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("portal/broadcasts.json")
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setCampaigns(result.data.data.campaigns);
            const rows = buildRateRows(result.data.data.broadcasts);
            setBroadcasts(rows);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function createBroadcast() {
    if (subject === null) {
      setSeverity("error");
      setErrMsg("Name is required.");
      setOpen(true);
      return;
    }

    if (selectedCampaign === null) {
      setSeverity("error");
      setErrMsg("Campaign is required");
      setOpen(true);
      return;
    }

    // const formattedTime = startTime.replace("T", " ");
    const json = {
      campaign: selectedCampaign,
      subject: `${subject}`,
      message_body: messageBody,
      channels: `${channels}`,
      audience: `${audience}`,
      frequency: `${frequency}`,
      interval: `${interval}`,
      // start_time: `${formattedTime}:00`,
    };

    console.log(json);

    setOpenNewCheckDialog(false);
    setShowLoading(true);

    await axios
      .post("portal/broadcasts/create.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openNewCheckDialog}>
        <DialogTitle>
          <MDBox>New Messaging Campaign</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox sx={{ marginTop: "15px" }}>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Select Campaign</InputLabel>
                <Select
                  label="Select Campaign"
                  onChange={(e) => {
                    setSelectedCampaign(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  {
                    // --> Notice below changes
                    campaigns.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                      campaigns.map((record) => (
                        <MenuItem value={record.reference}>{record.title}</MenuItem>
                      ))
                  }
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                placeholder="Subject"
                name="subject"
                margin="dense"
                label="Message Subject"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setSubject(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <Editor
                value={messageBody}
                onChange={(e) => setMessageBody(e.target.value)}
                placeholder="Message body"
              />
            </MDBox>
            <MDBox mt={4}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Customer Audience</InputLabel>
                <Select
                  label="Customer Audience"
                  onChange={(e) => {
                    setAudience(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  <MenuItem value="CAMPAIGN">Campaign</MenuItem>
                  <MenuItem value="ACCOUNT">Account</MenuItem>
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                placeholder="E.g EMAIL, SMS, WHATSAPP"
                name="channels"
                margin="dense"
                label="Message Channels (comma separated)"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setChannels(e.target.value)}
              />
            </MDBox>
            <MDBox mt={4}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Frequency</InputLabel>
                <Select
                  label="Frequency"
                  onChange={(e) => {
                    setFrequency(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  <MenuItem value="ONE_OFF">One Off</MenuItem>
                  <MenuItem value="REPEAT">Repeat</MenuItem>
                </Select>
              </FormControl>
            </MDBox>
            {frequency === "REPEAT" ? (
              <MDBox mt={4}>
                <FormControl sx={{ minWidth: 120, width: "100%" }}>
                  <InputLabel>Interval</InputLabel>
                  <Select
                    label="Interval"
                    onChange={(e) => {
                      setInterval(e.target.value);
                    }}
                    sx={{ padding: "12px", paddingLeft: "3px" }}
                  >
                    <MenuItem value="DAILY">Daily</MenuItem>
                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </MDBox>
            ) : (
              <> </>
            )}
            {/* <MDBox mt={2}>
              <MDInput
                placeholder="Start Time"
                name="start_time"
                margin="dense"
                label="Start Time"
                type="datetime-local"
                fullWidth
                variant="outlined"
                onChange={(e) => setStartTime(e.target.value)}
              />
            </MDBox> */}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenNewCheckDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="outlined"
            onClick={() => {
              createBroadcast();
            }}
          >
            Create Messaging
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <MDBox>
                  <Card sx={{ height: "100%", marginTop: "0px" }}>
                    <MDBox p={3}>
                      <MDBox
                        pt={2}
                        px={2}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <MDTypography variant="h6" fontWeight="medium">
                          Messaging Campaigns
                        </MDTypography>
                        <MDBox>
                          <MDButton
                            variant="contained"
                            color="dark"
                            onClick={() => {
                              setSelectedCampaign(null);
                              setSubject(null);
                              setMessageBody(null);
                              setChannels(null);
                              setAudience(null);
                              setFrequency(null);
                              setInterval(null);
                              setOpenNewCheckDialog(true);
                            }}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                            &nbsp;new messaging
                          </MDButton>
                        </MDBox>
                      </MDBox>
                      <MDBox mt={5}>
                        <DataTable
                          table={{ columns: tableColumns, rows: broadcasts }}
                          isSorted={false}
                          entriesPerPage={false}
                          showTotalEntries={false}
                          noEndBorder
                        />
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default BroadcastCampaign;
